import type {
  ProductAnalyticsAction,
  ProductAnalyticsFeature,
  ProductAnalyticsProperties,
} from '@fluenceur/types';
import { ProductAnalyticsClient } from '@fluenceur/ui';
import { isAxiosError } from 'axios';
import {
  analyticsSuppressionHeaders,
  analyticsVisitorId,
  readAnalyticsChoice,
} from './analytics-choice';
import { tokenManager } from './token-manager';

export const productAnalytics = new ProductAnalyticsClient({
  send: async (event, properties) => {
    const authenticated = tokenManager.isAuthenticated();
    const authorization = authenticated ? tokenManager.getAuthorizationHeader() : null;
    if (authenticated ? !authorization : readAnalyticsChoice() !== 'granted') return;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-product-analytics-surface': 'dashboard',
      ...(authorization
        ? { Authorization: authorization, ...analyticsSuppressionHeaders() }
        : { 'x-product-analytics-consent': 'granted' }),
    };
    const path = authenticated ? '/dashboard/analytics/events' : '/analytics/public-events';
    const body = authenticated
      ? { event, properties }
      : { event, properties, visitorId: analyticsVisitorId(), surface: 'dashboard' };
    const origin = (import.meta.env.VITE_API_URL || 'https://api.fluenceur.local').replace(
      /\/+$/,
      ''
    );
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5_000);
    try {
      // Optional events never enter global auth/refresh/paywall interceptors.
      // Credentials and refusal headers belong to the initiating session only.
      await fetch(`${origin}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'omit',
        redirect: 'error',
        referrerPolicy: 'no-referrer',
        keepalive: true,
        signal: controller.signal,
      });
    } catch {
      // Analytics failures have no session or UI effects and are never retried.
    } finally {
      clearTimeout(timeout);
    }
  },
});

export function resetProductAnalytics() {
  productAnalytics.reset();
}

function captureProductFailure(
  capture: ReturnType<ProductAnalyticsClient['captureForCurrentConsent']>,
  feature: ProductAnalyticsFeature,
  action: ProductAnalyticsAction,
  error: unknown
) {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  const outcome: ProductAnalyticsProperties['outcome'] =
    status === 402
      ? 'plan_refused'
      : status === 401 || status === 403
        ? 'permission_refused'
        : status && status >= 400 && status < 500
          ? 'business_refused'
          : 'technical_failure';
  capture(outcome === 'technical_failure' ? 'feature_failed' : 'feature_refused', {
    feature,
    action,
    outcome,
  });
}

/** Capture one explicit attempt and guard its later failure against consent/session changes. */
export function beginProductAction(
  feature: ProductAnalyticsFeature,
  action: ProductAnalyticsAction
) {
  const capture = productAnalytics.captureForCurrentConsent();
  capture('feature_attempted', { feature, action });
  return (error: unknown) => captureProductFailure(capture, feature, action, error);
}

/** Action boundaries only: never query functions, DTOs, response text, or mutation payloads. */
export async function trackProductAction<T>(
  feature: ProductAnalyticsFeature,
  action: ProductAnalyticsAction,
  run: () => Promise<T>
): Promise<T> {
  const captureFailure = beginProductAction(feature, action);
  try {
    return await run();
  } catch (error) {
    captureFailure(error);
    throw error;
  }
}
